// @ts-nocheck
import { useState, useEffect, useMemo, useRef } from 'react';
import { TableFieldPreviewProps } from 'pages/Forms/RegisterForm/CustomFormBuilder/Header/type/preview.type';

type InputType = 'yesno' | 'yesnona' | 'radiogroup' | 'checkbox';

interface useOptionsInputs extends TableFieldPreviewProps {
  inputType: InputType;
}

const isRadioType = (type: InputType): boolean =>
  ['yesno', 'yesnona', 'radiogroup'].includes(type);

const useOptionsInputs = ({
  field,
  updateAnswer,
  tableId,
  tableType,
  rowIndex,
  colIndex,
  updatePreviewColumn,
  inputType,
  updateAnswerInPrefilledElements,
}: useOptionsInputs) => {
  const textFieldRef = useRef<HTMLInputElement>(null);
  const [otherText, setOtherText] = useState<string>('');
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [answerValue, setAnswerValue] = useState<string>('');

  const answer = field?.answer as string | undefined;

  useEffect(() => {
    if (answer) {
      const values =
        inputType === 'checkbox'
          ? answer.split(',').filter((ans) => ans.trim() !== '')
          : [answer];

      setSelectedValues(values);

      const otherField = field.choice?.find((choice) => choice.isOtherField);
      if (otherField && values.includes(otherField.id)) {
        setOtherText(field?.selectedLinkListLabel || '');
      } else {
        setOtherText('');
      }
    } else {
      setSelectedValues([]);
      setOtherText('');
    }
  }, [answer, field, inputType]);

  const showActionMessage = useMemo(
    () =>
      field?.choice?.some(
        (choice) => choice.action && selectedValues.includes(choice.id),
      ) ?? false,
    [selectedValues, field?.choice],
  );

  const handleChange = (value: string) => {
    let newValues: string[];

    if (isRadioType(inputType)) {
      newValues = [value];
    } else {
      newValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
    }

    setSelectedValues(newValues);
    const answerValue = inputType === 'checkbox' ? newValues.join(',') : value;
    updateAnswer(tableId, rowIndex, colIndex, answerValue);
    setAnswerValue(answerValue);
    if (updateAnswerInPrefilledElements) {
      updateAnswerInPrefilledElements(
        tableId,
        answerValue,
        tableType,
        otherText,
        {
          rowIndex,
          colIndex,
          tableElementType: field?.type,
        },
      );
    }

    const otherField = field.choice?.find((choice) => choice.isOtherField);
    if (otherField && !newValues.includes(otherField.id)) {
      setOtherText('');
      updatePreviewColumn(tableId, rowIndex, colIndex, {
        selectedLinkListLabel: '',
      });
    }

    if (otherField && newValues.includes(otherField.id)) {
      setTimeout(() => textFieldRef.current?.focus(), 0);
    }
  };

  const handleOtherTextChange = (value: string) => {
    setOtherText(value);
    updatePreviewColumn(tableId, rowIndex, colIndex, {
      selectedLinkListLabel: value,
    });
    if (updateAnswerInPrefilledElements) {
      updateAnswerInPrefilledElements(tableId, answerValue, tableType, value, {
        rowIndex,
        colIndex,
        tableElementType: field?.type,
      });
    }
  };

  const clearSelection = () => {
    setSelectedValues([]);
    setOtherText('');
    updateAnswer(tableId, rowIndex, colIndex, '');
    setAnswerValue('');
    if (updateAnswerInPrefilledElements) {
      updateAnswerInPrefilledElements(tableId, '', tableType, '', {
        rowIndex,
        colIndex,
        tableElementType: field?.type,
      });
    }
  };

  return {
    otherText,
    selectedValues,
    showActionMessage,
    handleChange,
    handleOtherTextChange,
    clearSelection,
    isSelected: (value: string) => selectedValues.includes(value),
    textFieldRef,
  };
};

export default useOptionsInputs;
